/**
 * Unseen-share count for the switcher entry point (SB-5).
 *
 * The switcher's "New" chip only helps someone who already opened the switcher —
 * and a recipient with no notification has no reason to. The count on the
 * ENTRY POINT is what gives them one, so it has to exist before the modal does.
 *
 * `listProjects()` was previously called only inside the switcher's own load, so
 * there was no pre-open source for it. This module is that source: one list
 * fetch after auth, cached, re-derived whenever the switcher refreshes it or the
 * profile watermark moves.
 *
 * Deliberately NOT a poll. A share is not time-critical, and a background timer
 * hitting the list route forever is a cost with no ceiling; the count refreshes
 * at boot, when the switcher loads, and after the watermark advances.
 */

import { eventBus } from './events.js';
import { getProfile } from './profile-store.js';
import { unseenShareCount } from './unseen-shares.js';

let _projects = null;
let _count = 0;

function recompute() {
  const next = _projects ? unseenShareCount(_projects, getProfile()?.sharesSeenAt) : 0;
  if (next === _count) return _count;
  _count = next;
  eventBus.emit('shares:unseen-changed', { count: _count });
  return _count;
}

/** Sync read. 0 until the first list lands, which is the right default. */
export function getUnseenShareCount() {
  return _count;
}

/**
 * Adopt a project list the caller already has — the switcher passes its own
 * load result so opening the modal costs no extra request.
 */
export function adoptProjectList(projects) {
  _projects = Array.isArray(projects) ? projects : null;
  return recompute();
}

/**
 * Fetch the list and recompute. Best-effort: a failure leaves the previous
 * count rather than blanking the badge, because "we could not check" is not the
 * same as "there is nothing new" — the conflation this whole initiative exists
 * to remove.
 */
export async function refreshUnseenShares() {
  try {
    const { listProjects } = await import('../templates/auth-api.js');
    const resp = await listProjects();
    return adoptProjectList(resp?.projects || []);
  } catch {
    // Expected flow: offline, signed out, or the list route unavailable. The
    // badge is an accelerator, never the only way to find a shared project.
    return _count;
  }
}

/** Recompute against the current watermark without refetching. */
export function recomputeUnseenShares() {
  return recompute();
}

/** Test seam. */
export function _resetShareInboxForTesting() {
  _projects = null;
  _count = 0;
}
